/**
 * useTaxonomies Hook
 *
 * Provides taxonomy management for property types, amenities, and locations
 * using real Firestore queries.
 */

import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  collection,
  query,
  orderBy,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  writeBatch,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from '@/services/firebase';

export interface TaxonomyItem {
  id: string;
  name: string;
  slug: string;
  icon?: string;
  description?: string;
  category?: string;
  isPopular?: boolean;
  usageCount: number;
  order: number;
  createdAt: Date;
}

export interface Location {
  id: string;
  name: string;
  slug: string;
  type: 'country' | 'governorate' | 'district' | 'city';
  parentId?: string;
  coordinates?: { lat: number; lng: number };
  listingCount: number;
}

export interface CancellationPolicy {
  id: string;
  name: string;
  description: string;
  refundPercentage: number;
  deadline: string;
  isDefault?: boolean;
}

const COLLECTION_MAP: Record<string, string> = {
  propertyTypes: 'propertyTypes',
  roomTypes: 'roomTypes',
  amenities: 'amenities',
  locations: 'locations',
  policies: 'cancellationPolicies',
};

const toDate = (val: any): Date => {
  if (val instanceof Timestamp) return val.toDate();
  if (val instanceof Date) return val;
  return val ? new Date(val) : new Date();
};

export const useTaxonomies = (type: 'propertyTypes' | 'roomTypes' | 'amenities' | 'locations' | 'policies') => {
  const [items, setItems] = useState<TaxonomyItem[] | Location[] | CancellationPolicy[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const collectionName = COLLECTION_MAP[type];

  const fetchItems = useCallback(async () => {
    setIsLoading(true);
    try {
      const q = query(collection(db, collectionName), orderBy('order', 'asc'));
      const snapshot = await getDocs(q);

      if (snapshot.empty) {
        // If no data in Firestore yet, return empty
        setItems([]);
        setIsLoading(false);
        return;
      }

      if (type === 'locations') {
        setItems(
          snapshot.docs.map((d) => {
            const data = d.data();
            return {
              id: d.id,
              name: data.name || '',
              slug: data.slug || '',
              type: data.type || 'city',
              parentId: data.parentId,
              coordinates: data.coordinates,
              listingCount: data.listingCount || 0,
            } as Location;
          })
        );
      } else if (type === 'policies') {
        setItems(
          snapshot.docs.map((d) => {
            const data = d.data();
            return {
              id: d.id,
              name: data.name || '',
              description: data.description || '',
              refundPercentage: data.refundPercentage || 0,
              deadline: data.deadline || '',
              isDefault: data.isDefault || false,
            } as CancellationPolicy;
          })
        );
      } else {
        setItems(
          snapshot.docs.map((d) => {
            const data = d.data();
            return {
              id: d.id,
              name: data.name || '',
              slug: data.slug || '',
              icon: data.icon,
              description: data.description,
              category: data.category,
              isPopular: data.isPopular || false,
              usageCount: data.usageCount || 0,
              order: data.order || 0,
              createdAt: toDate(data.createdAt),
            } as TaxonomyItem;
          })
        );
      }
      setIsLoading(false);
    } catch (err) {
      console.error(`Failed to fetch ${type}:`, err);
      setItems([]);
      setIsLoading(false);
    }
  }, [type, collectionName]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const addItem = useCallback(async (data: Partial<TaxonomyItem>) => {
    const newDoc = await addDoc(collection(db, collectionName), {
      name: data.name || '',
      slug: data.slug || data.name?.toLowerCase().replace(/\s+/g, '-') || '',
      icon: data.icon || null,
      category: data.category || null,
      description: data.description || null,
      isPopular: data.isPopular || false,
      usageCount: 0,
      order: (items as TaxonomyItem[]).length + 1,
      createdAt: serverTimestamp(),
    });

    const newItem: TaxonomyItem = {
      id: newDoc.id,
      name: data.name || '',
      slug: data.slug || data.name?.toLowerCase().replace(/\s+/g, '-') || '',
      icon: data.icon,
      category: data.category,
      description: data.description,
      isPopular: data.isPopular || false,
      usageCount: 0,
      order: (items as TaxonomyItem[]).length + 1,
      createdAt: new Date(),
    };

    setItems((prev) => [...(prev as TaxonomyItem[]), newItem]);
    return newItem;
  }, [collectionName, items]);

  const updateItem = useCallback(async (id: string, data: Partial<TaxonomyItem>) => {
    await updateDoc(doc(db, collectionName, id), {
      ...data,
      updatedAt: serverTimestamp(),
    });

    setItems((prev) =>
      (prev as TaxonomyItem[]).map((item) =>
        item.id === id ? { ...item, ...data } : item
      ) as TaxonomyItem[]
    );
  }, [collectionName]);

  const deleteItem = useCallback(async (id: string) => {
    const item = (items as TaxonomyItem[]).find((i) => i.id === id);
    if (item && item.usageCount > 0) {
      throw new Error('Cannot delete item that is in use');
    }

    await deleteDoc(doc(db, collectionName, id));
    setItems((prev) => (prev as TaxonomyItem[]).filter((item) => item.id !== id));
  }, [collectionName, items]);

  const reorderItems = useCallback(async (orderedIds: string[]) => {
    const batch = writeBatch(db);
    orderedIds.forEach((id, index) => {
      batch.update(doc(db, collectionName, id), { order: index + 1 });
    });
    await batch.commit();

    setItems((prev) => {
      const itemMap = new Map((prev as TaxonomyItem[]).map((i) => [i.id, i]));
      return orderedIds
        .map((id, index) => {
          const item = itemMap.get(id);
          return item ? { ...item, order: index + 1 } : null;
        })
        .filter(Boolean) as TaxonomyItem[];
    });
  }, [collectionName]);

  return {
    items,
    isLoading,
    addItem,
    updateItem,
    deleteItem,
    reorderItems,
  };
};

export const useAmenityCategories = () => {
  const [categories, setCategories] = useState<string[]>([]);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const snapshot = await getDocs(collection(db, 'amenities'));
        const cats = new Set<string>();
        snapshot.docs.forEach((d) => {
          const cat = d.data().category;
          if (cat) cats.add(cat);
        });
        setCategories(Array.from(cats));
      } catch {
        setCategories([]);
      }
    };
    fetchCategories();
  }, []);

  return useMemo(() => categories, [categories]);
};

export default useTaxonomies;
